/**
 * Boot watchdog — what happens when the first navigation takes too long.
 *
 * The router's first navigation awaits `auth.init()` and `loadPermissions()`,
 * i.e. at least two network round trips (POST /api/auth/db-jwt, GET
 * role_permissions) before anything renders. On a settling connection, a
 * cold backend or a discarded tab waking up, that can take longer than a
 * user will stare at a white page.
 *
 * Until Sep 2026 this watchdog answered a slow boot with
 * `location.replace('/login?reset=1')` after 5s — which WIPED the Entra
 * session from the shared localStorage. Every other open tab then found the
 * key gone and logged "[auth] Entra session expired in background". A slow
 * boot is not a dead session; the fix is to say "still connecting" and let
 * the boot finish, and to make the wipe a choice the user makes.
 *
 * So on fire this renders a dependency-free overlay (plain DOM — Vue may not
 * have mounted, i18n may not be ready) with Reload and Reset-session
 * buttons, and removes it by itself the moment the router completes. It
 * never navigates and never touches storage on its own.
 */
import { trackEvent as aiTrackEvent } from '@/services/telemetryService'

export interface BootWatchdogOptions {
  /** Anything with `afterEach` — the app router. */
  router: { afterEach: (guard: () => void) => unknown }
  /** ms before the overlay appears. */
  budgetMs?: number
  /** Where the overlay is appended. Defaults to document.body. */
  mount?: HTMLElement
  /** "Reload" button. Defaults to `location.reload()`. */
  onRetry?: () => void
  /** "Reset session" button. Defaults to a `?reset=1&reason=boot_timeout` boot. */
  onReset?: () => void
}

export interface BootWatchdog {
  /** Show the overlay now (used when app.mount itself throws). */
  fireNow(): void
  /** Stop the timer and remove the overlay. */
  dispose(): void
  readonly fired: boolean
  readonly booted: boolean
}

export const BOOT_WATCHDOG_BUDGET_MS = 8_000
export const OVERLAY_ID = 'eh-boot-watchdog'

const STRINGS = {
  de: {
    slowTitle: 'Die Verbindung zum Server dauert länger als üblich',
    offlineTitle: 'Du scheinst offline zu sein',
    body: 'Deine Anmeldung bleibt erhalten. Warte einen Moment oder lade die Seite neu.',
    retry: 'Neu laden',
    reset: 'Sitzung zurücksetzen',
    resetHint: 'meldet dich ab',
  },
  en: {
    slowTitle: 'Connecting to the server is taking longer than usual',
    offlineTitle: 'You appear to be offline',
    body: 'Your sign-in is kept. Wait a moment or reload the page.',
    retry: 'Reload',
    reset: 'Reset session',
    resetHint: 'signs you out',
  },
}

function pickLanguage(): keyof typeof STRINGS {
  try {
    return (navigator.language || '').toLowerCase().startsWith('de') ? 'de' : 'en'
  } catch {
    return 'en'
  }
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

export function installBootWatchdog(opts: BootWatchdogOptions): BootWatchdog {
  const budgetMs = opts.budgetMs ?? BOOT_WATCHDOG_BUDGET_MS
  const onRetry = opts.onRetry ?? (() => { window.location.reload() })
  const onReset = opts.onReset ?? (() => { window.location.assign('/login?reset=1&reason=boot_timeout') })

  let booted = false
  let fired = false
  let overlay: HTMLElement | null = null
  let title: HTMLElement | null = null
  let timer: ReturnType<typeof setTimeout> | null = setTimeout(fire, budgetMs)

  const t = STRINGS[pickLanguage()]

  function syncTitle(): void {
    if (title) title.textContent = isOffline() ? t.offlineTitle : t.slowTitle
  }
  const onOnlineChange = () => syncTitle()

  function fire(): void {
    timer = null
    if (booted || fired) return
    fired = true
    const online = !isOffline()
    console.warn(`[boot watchdog] first navigation not complete after ${budgetMs}ms (online=${online}) — showing "still connecting", session untouched`)
    try { aiTrackEvent('boot_slow', { budget_ms: String(budgetMs), online: String(online) }) } catch { /* never block */ }
    render()
  }

  function render(): void {
    if (typeof document === 'undefined') return
    const host = opts.mount ?? document.body
    overlay = document.createElement('div')
    overlay.id = OVERLAY_ID
    overlay.setAttribute('role', 'alertdialog')
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;' +
      'background:#f9fafb;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#111827;padding:16px;'

    const card = document.createElement('div')
    card.style.cssText =
      'background:#fff;border-radius:16px;max-width:420px;width:100%;box-shadow:0 4px 24px rgba(0,0,0,.08);padding:32px 28px;text-align:center;'

    const spinner = document.createElement('div')
    spinner.style.cssText =
      'width:36px;height:36px;border:3px solid #e5e7eb;border-top-color:#111827;border-radius:50%;margin:0 auto 18px;animation:eh-boot-spin .8s linear infinite;'
    const style = document.createElement('style')
    style.textContent = '@keyframes eh-boot-spin{to{transform:rotate(360deg)}}'

    title = document.createElement('p')
    title.style.cssText = 'font-size:15px;font-weight:600;margin:0 0 8px;'
    syncTitle()

    const body = document.createElement('p')
    body.style.cssText = 'font-size:13px;color:#6b7280;margin:0 0 20px;line-height:1.5;'
    body.textContent = t.body

    const actions = document.createElement('div')
    actions.style.cssText = 'display:flex;gap:10px;justify-content:center;flex-wrap:wrap;'

    const retry = document.createElement('button')
    retry.type = 'button'
    retry.dataset.action = 'retry'
    retry.textContent = t.retry
    retry.style.cssText =
      'background:#111827;color:#fff;border:0;border-radius:8px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;'
    retry.addEventListener('click', () => onRetry())

    const reset = document.createElement('button')
    reset.type = 'button'
    reset.dataset.action = 'reset'
    reset.textContent = `${t.reset} (${t.resetHint})`
    reset.style.cssText =
      'background:transparent;color:#6b7280;border:1px solid #e5e7eb;border-radius:8px;padding:9px 16px;font-size:13px;cursor:pointer;'
    reset.addEventListener('click', () => onReset())

    actions.append(retry, reset)
    card.append(style, spinner, title, body, actions)
    overlay.append(card)
    host.append(overlay)

    window.addEventListener('online', onOnlineChange)
    window.addEventListener('offline', onOnlineChange)
  }

  function remove(): void {
    if (overlay) {
      overlay.remove()
      overlay = null
      title = null
      window.removeEventListener('online', onOnlineChange)
      window.removeEventListener('offline', onOnlineChange)
    }
  }

  opts.router.afterEach(() => {
    booted = true
    if (timer) { clearTimeout(timer); timer = null }
    remove()
  })

  return {
    fireNow: () => { if (timer) { clearTimeout(timer); timer = null }; fire() },
    dispose: () => { if (timer) { clearTimeout(timer); timer = null }; remove() },
    get fired() { return fired },
    get booted() { return booted },
  }
}
